import React, { useEffect, useState } from "react";
import { Paper, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { SearchParams } from "../../core/models/consumptionsBillsSearchBar/consumptionsBillsSearchBar.model";
import DatePickersComponent from "../UI/DatePickersComponent/DatePickersComponent";
import ButtonElement from "../UI/ButtonElement/ButtonElement";
import { OwnerDataCups } from "../../core/models/ownerData/ownerData.model";
import { GetGraphDataRequestBody } from "../../core/models/consumptions/consumptions.model";
import { addDayDate, formatDateString } from "../../utils/dateUtils/formatDate.util";
import "./ConsumptionsBillsSearchBar.scss";
import { initialOwnerDataCup } from "../../utils/ownerData/ownerData.util";
import ConsumptionsManagementsDeliveryAddressComponent from "../ConsumptionsManagementsDeliveryAddressComponent/ConsumptionsManagementsDeliveryAddressComponent";
import { SuppliesTableRow } from "../../core/models/supplies/supplies.model";

interface ConsumptionsBillsSearchBarProps {
  currentCup?: SuppliesTableRow | null;
  ownerDataCups: OwnerDataCups[];
  handleClickSearch: (requestBody: GetGraphDataRequestBody) => void;
  searchParams: SearchParams;
  setSearchParams: React.Dispatch<React.SetStateAction<SearchParams>>;
  isLoading: boolean;
}

const ConsumptionsBillsSearchBar = ({
  currentCup,
  ownerDataCups,
  handleClickSearch,
  searchParams,
  setSearchParams,
  isLoading,
}: ConsumptionsBillsSearchBarProps) => {
  const { t } = useTranslation();
  const [searchParamsSearchBar, setSearchParamsSearchBar] =
    useState<SearchParams>({
      dateFrom: null,
      dateTo: null,
      ownerDataCup: initialOwnerDataCup,
    });
  const isTextButtonFirstLoad = Object.values(searchParams).some(
    (searchParam) => !searchParam
  );

  useEffect(() => {
    setSearchParamsSearchBar(searchParams);
  }, [searchParams]);

  const hasCodeCups = !!searchParamsSearchBar.ownerDataCup?.codeCups.length;
  const isButtonDisabled =
    !(hasCodeCups || currentCup) ||
    !searchParamsSearchBar.dateFrom ||
    !searchParamsSearchBar.dateTo ||
    isLoading;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const {
      dateFrom,
      dateTo,
      ownerDataCup: { idCups },
    } = searchParamsSearchBar;

    const requestBody: GetGraphDataRequestBody = {
      dateFrom: formatDateString(addDayDate(dateFrom?.$d as Date)).replace(",", ""),
      dateTo: formatDateString(addDayDate(dateTo?.$d as Date)).replace(",", ""),
      cupsId: currentCup ? currentCup.cups.cupId : idCups,
    };

    setSearchParams(searchParamsSearchBar);
    handleClickSearch(requestBody);
  };

  const formatCurrentCup = ({
    cif,
    town,
    socialReason,
    rate,
    suppliesDirection,
    date,
    envirorment,
    cups: { cupId, contractId, contractState, cupCode, contractCode },
  }: SuppliesTableRow): OwnerDataCups => ({
    codeCups: cupCode,
    idCups: cupId,
    idContract: contractId,
    cif,
    envirorment,
    fee: rate,
    address: suppliesDirection,
    town,
    contractCode,
    socialReason,
    date: date as string,
    situation: contractState,
  });

  return (
    <Paper sx={{ width: "100%", borderRadius: "12px" }}>
      <Stack padding={{ xs: "1rem", md: "2rem" }}>
        <form
          onSubmit={(event) => handleSubmit(event)}
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: "1.5rem",
            width: "100%",
          }}
          className="form-stack"
        >
          <Stack width={{ xs: "100%", md: "30%" }}>
            <ConsumptionsManagementsDeliveryAddressComponent
              currentCup={currentCup ? formatCurrentCup(currentCup) : undefined}
              ownerDataCups={ownerDataCups}
              ownerDataCupSelected={searchParamsSearchBar.ownerDataCup}
              setOwnerDataCupSelected={(ownerDataCupSelected: OwnerDataCups) =>
                setSearchParamsSearchBar((prevSearchParamsSearchBar) => ({
                  ...prevSearchParamsSearchBar,
                  ownerDataCup: ownerDataCupSelected,
                }))
              }
            />
          </Stack>

          <DatePickersComponent
            billsSearchBar
            filters={searchParamsSearchBar}
            setFilters={(id, value) =>
              setSearchParamsSearchBar((prevSearchParamsSearchBar) => ({
                ...prevSearchParamsSearchBar,
                [id]: value,
              }))
            }
          />
          <Stack width={{ xs: "100%", md: "20%" }}>
            <ButtonElement disabled={isButtonDisabled}>
              {t(
                `Consumos.${
                  isTextButtonFirstLoad
                    ? "Mostrar gráfico"
                    : "Actualizar gráfico"
                }`
              )}
            </ButtonElement>
          </Stack>
        </form>
      </Stack>
    </Paper>
  );
};

export default ConsumptionsBillsSearchBar;
